import React, { useState } from "react";
import { Button, Icon } from "semantic-ui-react";
import { useLanguage } from "../../../../contexts";
import { P, textoSoloLectura, useEnergia, useGuardarSeccion, useModo, useSoloLectura } from "../engine";
import { ConInstalacion, SaveErrors, StatusBadge } from "../common/SectionCommon";
import { Grafico } from "../common/Grafico";
import "../common/common.scss";
import "./EnergyPerformanceDashboard.scss";

const fmt = (v, d = 0) => P.fmt(v, d);
const CLASE_DICTAMEN = { "badge-good": "Cumple", "badge-warn": "Brecha parcial", "badge-bad": "Brecha mayor" };

// Selección de gráficos por grupo (hoja 01, filas 97–98): casillas con título, cláusula, nota y marca A/B.
function Selector({ f, modo, elegidos, onCambiar, onRecomendada, t }) {
  const grupos = [...new Set(P.GRAFICOS.map((g) => g.grupo))];
  // sin permiso, las casillas se ven deshabilitadas con el motivo (hoja 23 p. 66: un lector no cambia la selección)
  const sinPermiso = useSoloLectura() === "permiso";
  return (
    <fieldset className="ep-dash__selector" disabled={sinPermiso}>
      {sinPermiso && <p className="ep-section__note">{textoSoloLectura(t, "permiso")}</p>}
      <div className="ep-pills">
        <Button type="button" basic size="small" onClick={onRecomendada}>{t("energy_performance_charts_recommended").replace("{uso}", modo)}</Button>
        <Button type="button" basic size="small" onClick={() => onCambiar(P.GRAFICOS.map((g) => g.id))}>{t("energy_performance_charts_all")}</Button>
        <Button type="button" basic size="small" onClick={() => onCambiar([])}>{t("energy_performance_charts_none")}</Button>
      </div>
      {grupos.map((gr) => (
        <fieldset key={gr} className="ep-dash__group">
          <legend>{gr}</legend>
          {P.GRAFICOS.filter((g) => g.grupo === gr).map((g) => (
            <label key={g.id} className="ep-dash__opt">
              <input type="checkbox" checked={elegidos.has(g.id)}
                onChange={(e) => { const s = new Set(elegidos); if (e.target.checked) s.add(g.id); else s.delete(g.id); onCambiar([...s]); }} />
              <span>
                <strong>{g.titulo}</strong> <span className="ep-chart__cl">{g.cl}</span>
                {P.GRAFICOS_REC.A.includes(g.id) && <span className="ep-dash__tag">A</span>}
                {P.GRAFICOS_REC.B.includes(g.id) && <span className="ep-dash__tag">B</span>}
                <span className="ep-section__note ep-dash__optnote">{g.nota}</span>
              </span>
            </label>
          ))}
        </fieldset>
      ))}
      <p className="ep-section__note">{t("energy_performance_charts_selector_note")}</p>
    </fieldset>
  );
}

// Dashboard (hoja 01, filas 96–101, 129): indicadores arriba y los gráficos elegidos del catálogo de 31 (hoja 25).
export function EnergyPerformanceDashboard({ installation, onSaved }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const modo = useModo();
  const vista = useEnergia(installation);
  const { guardar, errores } = useGuardarSeccion(installation, onSaved, { t });
  const [eligiendo, setEligiendo] = useState(false);

  return (
    <ConInstalacion installation={installation} vista={vista} t={t}>
      {(f) => {
        const r = vista.resultados;
        const d = P.dictamen(f);
        const impl = Object.values(r.implementacion_por_capitulo);
        const promImpl = impl.length ? impl.reduce((a, x) => a + x, 0) / impl.length : 0;
        const ahorro = (f.ecms || []).filter((e) => !P.esSuministro(e)).reduce((a, e) => a + (Number(e.kwh) || 0), 0);
        const implementadas = (f.ecms || []).filter((e) => e.status === "implementada").length;
        const sel = P.graficosSel(f, modo);
        const propia = !!(f.graficos && Array.isArray(f.graficos[modo]));
        const elegidos = new Set(sel.map((g) => g.id));
        const actuales = { ...(vista.inst.graficos || {}) };
        // la selección es de la planta (vale para todos sus usuarios) y se guarda al instante, por uso
        const cambiar = (ids) => guardar({ graficos: { ...actuales, [modo]: P.GRAFICOS.map((g) => g.id).filter((id) => ids.includes(id)) } }, { silencioso: true });
        const recomendada = () => { const g = { ...actuales }; delete g[modo]; guardar({ graficos: g }, { silencioso: true }); };

        return (
          <div className="ep-screen ep-dash">
            <h3 className="ep-screen__title">{t("energy_performance_dashboard_title")}</h3>
            <div className="ep-kpis">
              <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_index_title")}</div><div className="ep-kpi__value">{r.indice_mostrado}</div><div className="ep-kpi__sub">{P.scoreLbl(P.computeScore(f))}</div></div>
              <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_measurement_status")}</div><div className="ep-kpi__value ep-kpi__value--text"><StatusBadge estado={CLASE_DICTAMEN[d.cls] || "Pendiente"}>{d.nivel}</StatusBadge></div></div>
              <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_dashboard_consumption")}</div><div className="ep-kpi__value">{fmt(P.energiaAnual(f) / 1000, 1)}</div><div className="ep-kpi__sub">MWh · {t("energy_performance_last_12_months")}</div></div>
              <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_lben_type")}</div><div className="ep-kpi__value ep-kpi__value--text">{r.lben.etiqueta}</div><div className="ep-kpi__sub">{r.lben.cv != null ? `CV(RMSE) ${fmt(r.lben.cv, 1)}%` : ""}</div></div>
              <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_dashboard_implementation")}</div><div className="ep-kpi__value">{fmt(promImpl, 0)}%</div><div className="ep-kpi__sub">ISO 50001 · {t("energy_performance_dashboard_chapters")}</div></div>
              <div className="ep-kpi"><div className="ep-kpi__label">ECM</div><div className="ep-kpi__value">{fmt(ahorro)}</div><div className="ep-kpi__sub">kWh/{t("energy_performance_year")} · {implementadas} {t("energy_performance_ecm_implemented").toLowerCase()}</div></div>
            </div>

            <div className="ep-dash__bar">
              <span className="ep-section__note">
                {t("energy_performance_charts_summary").replace("{n}", sel.length).replace("{m}", P.GRAFICOS.length).replace("{uso}", modo === "A" ? t("energy_performance_use_a") : t("energy_performance_use_b"))}
                {!propia && ` · ${t("energy_performance_charts_recommended_short")}`}
              </span>
              <Button type="button" size="small" basic aria-expanded={eligiendo} onClick={() => setEligiendo(!eligiendo)}>
                <Icon name="sliders horizontal" /> {eligiendo ? t("energy_performance_charts_close") : t("energy_performance_charts_choose")}
              </Button>
            </div>
            {eligiendo && <Selector f={f} modo={modo} elegidos={elegidos} onCambiar={cambiar} onRecomendada={recomendada} t={t} />}
            <SaveErrors errores={errores} t={t} />

            {sel.length ? (
              <div className="ep-charts">{sel.map((g) => <Grafico key={g.id} f={f} g={g} t={t} />)}</div>
            ) : <p className="ep-section__note">{t("energy_performance_charts_empty")}</p>}
          </div>
        );
      }}
    </ConInstalacion>
  );
}
